import React from "react";
import { Wrench, Zap } from "lucide-react";

import { AppLogo } from "@/shared/components/AppLogo";
import { Chip } from "@/shared/components/ui-kit";

import type { AppAuth, AppRow } from "@/shared/types";

/** `none` is not "no auth configured" — the toolkit needs no account at all. */
export const AUTH_LABEL: Record<AppAuth, string> = {
  oauth2: "OAuth",
  api_key: "API key",
  none: "No connection needed",
};

/* One catalog row. The foot carries what decides whether the app is worth
   opening: how much it can do, whether it can start a run by itself, and what
   connecting it will cost you in consent screens. */
export function AppCard({ app, onOpen }: { app: AppRow; onOpen: (slug: string) => void }) {
  return (
    <button
      onClick={() => onOpen(app.slug)}
      className="card-hover press text-left rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] p-4 flex flex-col gap-3 min-w-0"
    >
      <div className="flex items-start gap-3">
        <AppLogo logo={app.logo} name={app.name} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-[14px] font-medium text-[var(--text-primary)] tracking-[-0.005em] truncate">
              {app.name}
            </span>
            {app.connected && <Chip tone="success">Connected</Chip>}
          </div>
          <div className="text-[11.5px] text-[var(--text-tertiary)] mt-[1px]">{app.category ?? "Other"}</div>
        </div>
      </div>
      <p className="text-[12.5px] leading-[1.55] text-[var(--text-secondary)] line-clamp-2 m-0">
        {app.description}
      </p>
      <div className="flex items-center gap-3 text-[11.5px] text-[var(--text-tertiary)] mt-auto">
        <span className="inline-flex items-center gap-1">
          <Wrench className="w-3 h-3" strokeWidth={1.75} />
          {app.tool_count} {app.tool_count === 1 ? "tool" : "tools"}
        </span>
        {app.trigger_count > 0 && (
          <span className="inline-flex items-center gap-1">
            <Zap className="w-3 h-3" strokeWidth={1.75} />
            {app.trigger_count} {app.trigger_count === 1 ? "trigger" : "triggers"}
          </span>
        )}
        <span className="ml-auto">{AUTH_LABEL[app.auth]}</span>
      </div>
    </button>
  );
}
